import CompanionAvatar from './CompanionAvatar'

type ChatPreviewMessage = {
  from: 'companion' | 'user'
  text: string
}

type Props = {
  messages: ChatPreviewMessage[]
  companionName?: string
  color?: string
  showTyping?: boolean
  className?: string
}

/**
 * Simulated chat conversation that staggers messages in, ending with a
 * live typing indicator — conveys "there's someone here to talk to".
 */
export default function ChatPreview({
  messages,
  companionName = 'Mia',
  color = '#A8937D',
  showTyping = true,
  className = '',
}: Props) {
  const typingDelay = messages.length * 0.7 + 0.4

  return (
    <div className={`flex w-full max-w-[340px] flex-col gap-2.5 ${className}`}>
      {messages.map((message, idx) => {
        const isCompanion = message.from === 'companion'
        return (
          <div
            key={idx}
            className={`flex items-end gap-2 ${isCompanion ? '' : 'flex-row-reverse'}`}
            style={{
              opacity: 0,
              animation: `messageIn 0.45s ease-out ${(idx * 0.7 + 0.3).toFixed(2)}s forwards`,
            }}
          >
            {isCompanion && (
              <CompanionAvatar
                name={companionName}
                color={color}
                size="small"
                online={false}
                animate={false}
                className="shrink-0 scale-75"
              />
            )}
            <div
              className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-left text-[0.9rem] leading-snug shadow-[0_2px_10px_rgba(43,34,48,0.06)] ${
                isCompanion
                  ? 'rounded-bl-md bg-white text-[#2B2230]'
                  : 'rounded-br-md bg-[#2B2230] text-white'
              }`}
            >
              {message.text}
            </div>
          </div>
        )
      })}

      {showTyping && (
        <div
          className="flex items-end gap-2"
          style={{
            opacity: 0,
            animation: `messageIn 0.45s ease-out ${typingDelay.toFixed(2)}s forwards`,
          }}
        >
          <CompanionAvatar
            name={companionName}
            color={color}
            size="small"
            online={false}
            animate={false}
            className="shrink-0 scale-75"
          />
          <div className="flex gap-1.5 rounded-2xl rounded-bl-md bg-white px-4 py-3.5 shadow-[0_2px_10px_rgba(43,34,48,0.06)]">
            {[0, 0.2, 0.4].map((delay) => (
              <span
                key={delay}
                className="h-2 w-2 rounded-full bg-[#C5AEB6]"
                style={{
                  animation: `typingBounce 1.4s ease-in-out ${delay}s infinite`,
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
